import { ResourceBookingEnum } from 'store/app-instance-configs/enums';
import { useCallback, useMemo } from 'react';

import { searchParams } from 'components/paymentIFrame/paymentIFrame.interface';
import { useLocale } from 'hooks/use-locale.hook';
import { useOwnerParams } from 'hooks/use-owner-params.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment-timezone';
import qs from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { OwnerType, RESOURCE_PATH, TRACK_EVENT_NAMES, TRACK_EVENT_TYPES } from 'shared/consts';
import { AddOnDto } from 'store/add-ons/types';
import { selectBookingRequest } from 'store/bookings/selectors';
import { createCart } from 'store/cart/actions';
import { LocaleEnum, OrderType } from 'store/cart/types';
import { configSelector } from 'store/config/selectors';
import { resetResourceTimeRanges } from 'store/resource-time-ranges/actions';
import { resourceState } from 'store/resource/selectors';
import { Resource, RoomLayout } from 'store/resource/types';
import { replace } from 'store/router/actions';
import { bookingTimeHandler } from 'utils/bookingTimeHandler';
import { UNICODE_CHARS } from 'utils/constants';
import { formatLocale } from 'utils/formatLocale';
import { getResourceTrackInfo } from 'utils/getResourceTrackInfo';
import { getUserLocale } from 'utils/getUserLocale';
import { resolveCartItems } from 'utils/resolveCartItems';

import { track } from '@hqo/web-tracking';
import { setBookingRequest } from 'store/bookings/actions';
import { useTrackingInfo } from 'hooks/use-tracking-information.hook';
import { appInstanceConfigsSelector, selectCurrentAppInstanceConfig } from 'store/app-instance-configs/selectors';
import { useAppInstanceConfigFeature } from 'hooks/use-app-instance-config-feature.hook';
import { getCurrentHighestDiscount } from 'components/discounts-list/hooks/utils/getCurrentHighestDiscount';
import { usePriceQuery } from 'data/queries/getPriceQuery.hook';
import { findNativeEnabledExchangeConfig } from 'utils/native-enabled-exchange';
import { AppInstanceConfig } from 'store/app-instance-configs/types';

interface SubmitBookingProps {
  availableNowStartDate?: string;
  availableNowStartTime?: string;
  availableNowEndTime?: string;
  availableNowResource?: Resource;
  currentRoomLayout?: RoomLayout;
  selectedAddOns?: Array<AddOnDto>;
  addOnsPrice?: number;
  unitCode?: string;
}

/*
 * Whether a booking on this adapter may carry the IN-CHECKOUT add-on slice into its cart.
 *
 * ⛔ Native may not. That slice is session-scoped and cleared at only a handful of sites, so anything left
 * in it from an earlier Prism or Nexudus booking would otherwise ride into a native cart — and the cart
 * resolves exactly one payment recipient, so it cannot carry both the landlord's room money and a
 * vendor's. Native add-ons are chosen after the booking exists and go to the Service Request path.
 *
 * ⛔ POSITIVELY non-native, not merely "not known to be native": the adapter name is undefined until
 * app-instance configs resolve, so anything unresolved carries none either.
 *
 * Exported so the rule can be asserted without driving this hook's ordered selector chain.
 */
export const canCarryCartAddOns = (adapterName: string | undefined | null): boolean =>
  Boolean(adapterName) && adapterName !== ResourceBookingEnum.NativeAdapter;

/*
 * The adapter for the resource actually being booked.
 *
 * ⚠️ Prefer the config for that resource's OWN building over the globally selected one. The app does
 * switch the active config when a resource is picked — the floorplan popup and the floorplan click hook
 * both dispatch it — but not every entry point does, and in a portfolio a tenant can reach a resource in
 * one building while the active config still points at another.
 *
 * ⭐ Matched on `location_id` or `alt_building_uuid`, NOT on `building_uuid`. `location_id` is the field
 * that holds a building uuid on an app-instance config — helix's own `BuildingPermissionGuard` resolves a
 * building uuid as `building_uuid || buildingUuid || location_id`, and `useResourceAppInstanceConfig` is
 * this repo's existing resolver, matching a resource exactly that way. A config's `building_uuid` is a
 * different field and is not populated on the configs this list is built from.
 *
 * ⚠️ `alt_building_uuid` is checked as well, and deliberately. It is a real column on the config
 * (`ALT_BUILDING_UUID`) and also carries a building uuid; the app's own mocked config sets it to the
 * mocked resource's building while giving `location_id` a numeric value. Since a miss here now fails
 * closed, accepting either identifier is what keeps an ordinary third-party booking from losing its
 * add-ons over which of the two a given config happens to populate. Both name the SAME config, so there
 * is no risk of matching the wrong one.
 *
 * ⛔ There are exactly two cases, and the active config is only consulted in one of them. A resource with
 * no building of its own is the ordinary single-building path, where the active config IS its config. A
 * resource that names a building is answered by that building's config alone, and an unresolved lookup
 * returns undefined, which fails closed. Borrowing the active adapter in that second case is what would
 * let a native resource reached under a Prism active config be judged cart-carrying.
 */
export const adapterForResource = (
  resourceBuildingUuid: string | undefined,
  configs:
    | Array<{ location_id?: string; alt_building_uuid?: string; vertical_adapter?: { name?: string } }>
    | undefined,
  activeAdapterName: string | undefined,
): string | undefined => {
  /*
   * No building of its own. This is the ordinary single-building path, where the active config IS the
   * resource's config, so using it is correct rather than a fallback.
   */
  if (!resourceBuildingUuid) {
    return activeAdapterName;
  }

  /*
   * ⛔ The resource named a building, so that building's config is the only authority on it. A miss returns
   * undefined and FAILS CLOSED rather than borrowing the active adapter — otherwise a native resource
   * reached while the active config is Prism would be judged cart-carrying, which is the leak this exists
   * to close. Failing closed costs nothing real: `add_ons_enabled` lives on the config, so a building with
   * no config cannot have had add-ons enabled and has no legitimate cart add-ons to preserve.
   */
  return configs?.find(
    config => config.location_id === resourceBuildingUuid || config.alt_building_uuid === resourceBuildingUuid,
  )?.vertical_adapter?.name;
};

/*
 * RES-1373 — the building a cart must be filed under for the active config.
 *
 * ⛔ A cart is looked up server-side by (config uuid, owner), so the owner has to be the building that owns
 * that uuid. `building_uuid` is not always that: helix normalizes the uuid of every shared portfolio location
 * onto the requested building's config but leaves `building_uuid` as the location's own building. While the
 * requested building shows its own location that entry comes first and the two agree; once it hides its
 * location (`hide_location`) the first match is another building and the cart is rejected with a 400.
 * helix names the real owner in `aic_owner_uuid`; without it (older helix, non-portfolio entries) the
 * entry's own building is right, as before.
 */
export const resolveCartOwnerUuid = (
  activeAppInstanceConfig: Pick<AppInstanceConfig, 'aic_owner_uuid' | 'building_uuid'> | undefined,
): string | undefined => activeAppInstanceConfig?.aic_owner_uuid ?? activeAppInstanceConfig?.building_uuid;

export const useOnSubmitBookingRequest = () => {
  const dispatch = useDispatch();
  const { ownerUuid } = useOwnerParams();
  const { startDate, startTime, endTime, startDates } = useSearchParams<searchParams>();
  const getBookingRequest = useSelector(selectBookingRequest);
  const { resource } = useSelector(resourceState);
  const isTripleseatIntegration = resource?.integration === 'tripleseat';
  const { exchangeIntegrationV2 } = useFlags();
  const { buildingUuid, locale: language } = useSelector(configSelector);
  const activeAppInstanceConfig = useSelector(selectCurrentAppInstanceConfig);
  /*
   * ⛔ The UNFILTERED list, deliberately — not `selectAppInstanceConfigs`. That selector narrows to a single
   * config in `singleAppInstanceConfig` mode and to the current location in `singleLocationConfig` mode, so
   * a resource in another building would find no match and be judged unresolvable when its config exists
   * perfectly well. The lookup below fails closed on a miss, so a narrowed list would turn a benign
   * cross-building case into a silently dropped add-on.
   */
  const allAppInstanceConfigs = useSelector(appInstanceConfigsSelector).app_instance_configs;

  const priceQuery = usePriceQuery({
    resourceId: resource.id,
    timezone: resource.timezone,
    enabled: !isTripleseatIntegration,
  });
  const price = priceQuery.data?.price;
  const locale = useLocale();
  const { source: trackingSource } = useTrackingInfo();

  const itemSubtitleText = useMemo<string | null>(() => {
    if (startTime && endTime && resource.timezone) {
      const startBookingDate = moment(startDate?.toString()).tz(resource.timezone, true);
      const startBookingTime = moment(startTime.toString(), 'H:m').tz(resource.timezone, true).format('HH:mm');

      const endBookingTime = moment(endTime.toString(), 'H:m').tz(resource.timezone, true).format('HH:mm');

      const timeRange = { startTime: startBookingTime as string, endTime: endBookingTime as string, locale };
      const startDateText = bookingTimeHandler(timeRange).getDateRangeString(
        startBookingDate.format('YYYY-MM-DD') as string,
      );
      const endDateText = bookingTimeHandler(timeRange).getTimeRangeString();

      return `${startDateText} ${UNICODE_CHARS.BULLET} ${endDateText}`;
    }

    return null;
  }, [startTime, endTime, resource, startDate, locale]);

  const discount = getCurrentHighestDiscount(startDate, resource?.discounts);

  return useCallback(
    (submitBookingProps: SubmitBookingProps = {}) => {
      track(
        TRACK_EVENT_NAMES.RESOURCE_BOOK_CLICK,
        {
          source: trackingSource,
          type: TRACK_EVENT_TYPES.ACTION,
          resource: getResourceTrackInfo(resource),
        },
        { sendToHqoTracking: true },
      );
      const {
        availableNowStartDate,
        availableNowStartTime,
        availableNowEndTime,
        availableNowResource,
        currentRoomLayout,
        selectedAddOns,
        unitCode,
      } = submitBookingProps;

      const resourceForCart = availableNowResource ?? resource;

      /*
       * RES-1050 — a native booking carries no cart add-ons. Guarded HERE rather than at the call sites:
       * FIVE call sites pass this slice into a booking submit — the room-layout step,
       * additional-information, the resource tile, available-now, and the in-checkout add-ons page itself —
       * so guarding any one of them implies a protection the other four lack. The fifth is the decisive
       * one: it lives in `src/pages/add-ons/**`, which serves ~130 live production buildings and is out of
       * bounds for this ticket. The cart is built once; the rule belongs with it, and beside the resource
       * it is actually built for.
       */
      const carriesCartAddOns = canCarryCartAddOns(
        adapterForResource(
          resourceForCart?.building_uuid,
          allAppInstanceConfigs,
          activeAppInstanceConfig?.vertical_adapter?.name,
        ),
      );
      /*
       * RES-1330: an Exchange-integration resource booked through the native adapter must be filed under the
       * Exchange config that has `native_resources_enabled`; under the native config alone the booking is
       * written natively and never reaches Exchange. `submitCart` replays this from the cart state.
       */
      const exchangeConfig = exchangeIntegrationV2
        ? findNativeEnabledExchangeConfig(
            resourceForCart,
            allAppInstanceConfigs,
            adapterForResource(
              resourceForCart?.building_uuid,
              allAppInstanceConfigs,
              activeAppInstanceConfig?.vertical_adapter?.name,
            ),
          )
        : undefined;
      /*
       * ⛔ RES-1373 — under an Exchange override the cart is filed under the Exchange config's uuid, and helix
       * looks that uuid up together with the cart owner, so the owner must be the Exchange config's own
       * building. Resolved from it first; the active config only decides when there is no override.
       */
      const cartOwnerUuid = resolveCartOwnerUuid(exchangeConfig) ?? resolveCartOwnerUuid(activeAppInstanceConfig);
      const lang = getUserLocale(language);
      const startDatesArray = startDates ? startDates?.split(',') : [];

      if (unitCode) {
        dispatch(
          setBookingRequest({
            ...getBookingRequest,
            unit_code: unitCode,
          }),
        );
      }

      dispatch(
        createCart.request({
          type: OrderType.RESOURCE_BOOKING,
          owner_type: OwnerType.BUILDING,
          owner_id: cartOwnerUuid ?? ownerUuid,
          building_uuid: cartOwnerUuid ?? buildingUuid,
          lang: formatLocale(lang) as LocaleEnum,
          items: resolveCartItems(
            [startDate, ...startDatesArray],
            {
              resource: resourceForCart,
              getBookingRequest,
              price,
              itemSubtitleText,
              currentRoomLayout,
              /*
               * `undefined`, not `[]` — `resolveCartItems` spreads this conditionally and an empty array is
               * truthy, so `[]` would add an `add_ons: []` key where the key was previously absent. That is
               * a wire change on every booking this strips, and the existing cart-payload test catches it.
               */
              selectedAddOns: carriesCartAddOns ? selectedAddOns : undefined,
              timeRangeParam: { startTime, endTime },
              availableNowParams: { availableNowStartTime, availableNowEndTime, availableNowStartDate },
              discount,
            },
            unitCode,
          ),
          locationId: resourceForCart.location.id,
          ...(exchangeConfig ? { appInstanceConfigUuid: exchangeConfig.uuid } : {}),
        }),
      );
    },
    [
      dispatch,
      ownerUuid,
      getBookingRequest,
      resource,
      startDate,
      startTime,
      endTime,
      price,
      buildingUuid,
      itemSubtitleText,
      language,
      activeAppInstanceConfig,
      /*
       * ⛔ Load-bearing, not housekeeping. Omitted, the callback keeps its MOUNT-time configs — which are
       * empty until they resolve — so the per-building lookup finds nothing and falls through to the
       * active adapter. That is the fail-open direction this guard exists to close.
       */
      allAppInstanceConfigs,
      exchangeIntegrationV2,
      startDates,
      trackingSource,
      discount,
    ],
  );
};

export const useRedirectToNextStep = () => {
  const dispatch = useDispatch();
  const { resource } = useSelector(resourceState);
  const { pathname } = useLocation();
  const hasAdditionalInfo = !!resource?.user_prompts?.length;
  const onSubmitBookingRequest = useOnSubmitBookingRequest();
  const params = useSearchParams();
  const { isCompactUiEnabled } = useAppInstanceConfigFeature();
  const isUserOnCompactUi = pathname.includes('/resources');
  const queryParams = qs.stringify({ ...params, step: undefined });
  const { showResourceDetailsDateTimeInputs } = useFlags();
  const { source: trackingSource } = useTrackingInfo();

  return useCallback(() => {
    if (isCompactUiEnabled && !hasAdditionalInfo) {
      dispatch(replace(`${pathname.replace('/date-schedule-select', '/')}?${queryParams}`));
      onSubmitBookingRequest();
    } else if (isCompactUiEnabled && hasAdditionalInfo && !pathname.includes('additional-information')) {
      dispatch(replace(`${RESOURCE_PATH}/${resource.uuid}/additional-information?${queryParams}`));
    } else if (
      (hasAdditionalInfo && !pathname.includes('additional-information')) ||
      pathname.includes('additional-information/date-schedule-select') ||
      resource?.room_layouts?.length ||
      resource?.add_ons?.length ||
      (showResourceDetailsDateTimeInputs && !isUserOnCompactUi)
    ) {
      dispatch(replace(`${pathname.replace('/date-schedule-select', '')}?${queryParams}`));
    } else {
      track(
        TRACK_EVENT_NAMES.RESOURCE_BOOK_CLICK,
        {
          type: TRACK_EVENT_TYPES.ACTION,
          source: trackingSource,
          resource: {
            type: resource.type,
            uuid: resource.uuid,
            resource_id: resource.id.toString(),
            name: resource.name,
            capacity: resource.capacity,
          },
        },
        { sendToHqoTracking: true },
      );
      dispatch(replace(`${pathname.replace('/date-schedule-select', '')}?${queryParams}`));
      onSubmitBookingRequest();
    }
    dispatch(resetResourceTimeRanges());
  }, [
    isCompactUiEnabled,
    hasAdditionalInfo,
    dispatch,
    pathname,
    queryParams,
    onSubmitBookingRequest,
    showResourceDetailsDateTimeInputs,
    isUserOnCompactUi,
    resource?.uuid,
    resource?.type,
    resource?.room_layouts,
    resource?.add_ons,
  ]);
};
